import { CartAddEvent } from '@theme/events';

const SELECTOR = '[data-tv-upsell-add]';
const CART_JSON_SECTION = 'trivelle-cart-json';

function getCartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.push(el.dataset.sectionId);
    }
  });
  return [...new Set([...ids, CART_JSON_SECTION])];
}

function cartFromSections(sections) {
  const html = sections && sections[CART_JSON_SECTION];
  if (!html) return undefined;
  try {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-tv-cart]');
    const cart = node && JSON.parse(node.textContent);
    return cart && Array.isArray(cart.items) ? cart : undefined;
  } catch {
    return undefined;
  }
}

function openDrawerPending(button) {
  if (document.querySelector('main cart-items-component')) return null;
  if (button && button.closest('dialog[open]:not(.cart-drawer__dialog)')) return null;
  if (document.querySelector('#tv-mat-pop[open]')) return null;
  const drawer = document.querySelector('cart-drawer-component');
  const dialog = drawer && drawer.querySelector('dialog');
  const items = drawer && drawer.querySelector('cart-items-component');
  if (!dialog || !items || typeof drawer.showDialog !== 'function') return null;
  const wasOpen = dialog.open;
  dialog.classList.add('tv-cart-adding');
  dialog.setAttribute('aria-busy', 'true');
  items.classList.add('cart-items-disabled');
  try {
    drawer.showDialog();
  } catch {}
  return { drawer, dialog, items, wasOpen };
}

function clearPending(pending) {
  if (!pending) return;
  pending.dialog.classList.remove('tv-cart-adding');
  pending.dialog.removeAttribute('aria-busy');
  pending.items = pending.drawer.querySelector('cart-items-component') || pending.items;
  pending.items.classList.remove('cart-items-disabled');
}

function refreshStickySummary(pending) {
  if (!pending || !pending.dialog.open) return;
  const { dialog } = pending;
  const summary = dialog.querySelector('.cart-drawer__summary');
  const content = dialog.querySelector('.cart-drawer__content');
  const ratio = summary && content ? summary.getBoundingClientRect().height / dialog.getBoundingClientRect().height : 1;
  dialog.setAttribute('cart-summary-sticky', ratio > 0.5 ? 'false' : 'true');
}

function showAddError(button, message) {
  const host =
    button.closest('dialog[open]') || document.querySelector('cart-drawer-component dialog[open]') || document.body;
  let toast = document.getElementById('tv-atc-toast');
  if (!toast) {
    toast = document.createElement('p');
    toast.id = 'tv-atc-toast';
    toast.className = 'tv-toast';
    toast.setAttribute('role', 'alert');
  }
  if (toast.parentNode !== host) host.appendChild(toast);
  toast.textContent = message;
  toast.hidden = false;
  clearTimeout(toast._tvHide);
  toast._tvHide = setTimeout(() => {
    toast.hidden = true;
  }, 7000);
}

function writeAttributionBeforeCheckout() {
  const write = window.__tvWriteCartAttributes;
  if (typeof write !== 'function') return Promise.resolve();
  const timeout = new Promise((resolve) => setTimeout(resolve, 1500));
  return Promise.race([Promise.resolve().then(write).catch(() => {}), timeout]);
}

function sourceOf(button) {
  if (button.dataset.tvSource) return button.dataset.tvSource;
  if (button.closest('.tv-du, .tv-dc')) return 'cart';
  const row = button.closest('.tv-ritual-row[data-context]');
  if (row) return row.dataset.context;
  if (button.closest('.tv-cart-extras')) return 'cart';
  if (button.closest('.tvc')) return 'collection';
  const sheet = button.closest('dialog[id^="tv-"]');
  if (sheet) return sheet.id === 'tv-mat-pop' ? 'matsheet' : 'popup';
  return 'menu';
}

async function handleClick(event) {
  const button = event.target.closest(SELECTOR);
  if (!button) return;
  event.preventDefault();

  if (button.hasAttribute('disabled')) return;
  const multi = button.hasAttribute('data-variant-ids');
  const variantId = button.dataset.variantId;
  const variantIds = multi
    ? button.dataset.variantIds.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  if (multi ? !variantIds.length : !variantId) return;

  const isAddon = button.hasAttribute('data-tv-addon');
  const toCheckout = button.hasAttribute('data-tv-checkout');
  let leaving = false;
  const originalLabel = button.innerHTML;
  button._tvLabel = originalLabel;
  button.setAttribute('disabled', '');
  button.setAttribute('aria-busy', 'true');
  button.textContent = toCheckout ? 'Taking you to checkout…' : 'Adding…';

  let pending = null;
  if (!toCheckout) {
    document.dispatchEvent(new CustomEvent('trivelle:upsell:adding', { detail: { button } }));
    pending = openDrawerPending(button);
  }

  const sectionIds = toCheckout ? [] : getCartSectionIds();
  const cartAddUrl =
    (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) || '/cart/add';

  let request;
  if (multi) {
    const payload = { items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })) };
    if (sectionIds.length) payload.sections = sectionIds.join(',');
    request = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    };
  } else {
    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));
    request = {
      method: 'POST',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    };
  }

  try {
    const response = await fetch(cartAddUrl, request);
    const data = await response.json();

    if (data && data.status) {
      console.warn('[trivelle] upsell add rejected:', data.message);
      clearPending(pending);
      if (pending && !pending.wasOpen) pending.drawer.closeDialog?.();
      if (!toCheckout) document.dispatchEvent(new CustomEvent('trivelle:upsell:addfail'));
      showAddError(button, data.description || data.message || 'This item could not be added.');
      return;
    }

    const added = data && Array.isArray(data.items) ? data.items : [data];
    const first = added[0] || {};

    if (isAddon) {
      try {
        sessionStorage.setItem('tv:ritual', 'done');
      } catch {}
    }

    if (toCheckout) {
      await writeAttributionBeforeCheckout();
      document.dispatchEvent(
        new CustomEvent('trivelle:track', {
          detail: { name: 'tv_buy_now', data: { productId: first.product_id, itemCount: added.length, source: sourceOf(button) } },
        })
      );
      leaving = true;
      window.location.href = '/checkout';
      return;
    }

    document.dispatchEvent(
      new CartAddEvent(cartFromSections(data && data.sections), 'trivelle-upsell', {
        source: 'trivelle-upsell',
        itemCount: added.length,
        productId: first.product_id,
        sections: data && data.sections,
      })
    );
    refreshStickySummary(pending);

    document.dispatchEvent(
      new CustomEvent('trivelle:track', {
        detail: {
          name: 'tv_cart_cross_sell_add',
          data: {
            productId: first.product_id,
            itemCount: added.length,
            addon: isAddon,
            source: sourceOf(button),
          },
        },
      })
    );
  } catch (error) {
    console.warn('[trivelle] upsell add failed, falling back to cart page', error);
    leaving = true;
    window.location.href = toCheckout ? '/checkout' : '/cart';
    return;
  } finally {
    clearPending(pending);
    if (!leaving) {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
      button.innerHTML = originalLabel;
    }
  }
}

document.addEventListener('click', handleClick);

let pdpPending = null;
let pdpPendingTimer = 0;

function settlePdpPending(failed) {
  const pending = pdpPending;
  pdpPending = null;
  clearTimeout(pdpPendingTimer);
  if (!pending) return;
  clearPending(pending);
  if (failed && !pending.wasOpen) pending.drawer.closeDialog?.();
  else requestAnimationFrame(() => refreshStickySummary(pending));
}

document.addEventListener('trivelle:pdp:adding', () => {
  settlePdpPending(false);
  pdpPending = openDrawerPending(null);
  if (pdpPending) pdpPendingTimer = setTimeout(() => settlePdpPending(false), 10000);
});
document.addEventListener('trivelle:pdp:addfail', () => settlePdpPending(true));

function openDrawerAfterOtherDialogs() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer || typeof drawer.showDialog !== 'function' || document.querySelector('main cart-items-component')) return;
  const open = () => {
    try {
      drawer.showDialog();
    } catch {}
  };
  const other = document.querySelector('dialog[open]:not(.cart-drawer__dialog)');
  if (!other) {
    open();
    return;
  }
  let opened = false;
  const once = () => {
    if (opened) return;
    opened = true;
    open();
  };
  other.addEventListener('close', once, { once: true });
  setTimeout(once, 1200);
}

document.addEventListener(CartAddEvent.eventName, (event) => {
  const data = event.detail && event.detail.data;
  if (data && data.source === 'product-form-component' && !data.didError) openDrawerAfterOtherDialogs();
});
document.addEventListener(CartAddEvent.eventName, (event) => {
  const detail = event.detail || {};
  if (detail.sourceId === 'trivelle-pdp' || detail.source === 'trivelle-pdp') settlePdpPending(false);
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll(`${SELECTOR}[aria-busy="true"]`).forEach((button) => {
    if (button._tvLabel == null) return;
    button.removeAttribute('disabled');
    button.removeAttribute('aria-busy');
    button.innerHTML = button._tvLabel;
  });
});
